import { getDefinition } from "./catalog";
import { makeRvPanel, rvPanelBranchCount, RV_MAX_BRANCHES, RV_PANEL_ID } from "./catalog/rv-distribution";
import type { ComponentDefinition, System } from "./types";

/** Resize only the reviewed panel shape, preserving IDs and every connected
 * circuit. Removing a wired branch is rejected rather than moving its cables. */
export function planRvPanelResize(system: System, nodeId: string, count: number): Partial<ComponentDefinition> | null {
  const node = system.nodes.find(n => n.id === nodeId);
  if (node?.kind !== "component" || !Number.isInteger(count) || count < 1 || count > RV_MAX_BRANCHES) return null;
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def || def.id !== RV_PANEL_ID) return null;
  const previousCount = rvPanelBranchCount(def);
  if (previousCount == null || previousCount === count) return null;
  const previous = makeRvPanel(previousCount);
  const next = makeRvPanel(count);
  const ids = new Set(next.terminals.map(t => t.id));
  if (system.cables.some(c =>
    (c.fromNodeId === nodeId && !ids.has(c.fromTerminalId)) ||
    (c.toNodeId === nodeId && !ids.has(c.toTerminalId)))) return null;
  return {
    // Reflow default positions, but keep deliberate terminal labels, sides
    // and offsets. Instance labels/overrides are retained by customization.
    terminals: next.terminals.map(terminal => {
      const existing = def.terminals.find(t => t.id === terminal.id);
      const original = previous.terminals.find(t => t.id === terminal.id);
      if (!existing || !original) return terminal;
      return { ...terminal, label: existing.label, color: existing.color,
        ...(existing.side !== original.side || existing.offset !== original.offset
          ? { side: existing.side, offset: existing.offset } : {}),
      };
    }),
    height: def.height === previous.height ? next.height : def.height,
    // Older generated panels used 360 px; retain any deliberate custom width.
    width: def.width === 360 ? next.width : def.width,
    subtitle: next.subtitle, simulateStates: next.simulateStates,
  };
}
